import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { DataTable } from '@bliks-io/ui';
import type { TaskListItem } from '@bliks-io/contracts';
import { useCompleteTask } from '@/api/mutations/useTaskActions';
import { toastApiError } from '@/api/mutations/toast-api-error';
import { useOpenTasksColumns } from './useOpenTasksColumns';
import { ApprovalDialog } from '@/components/approvals/ApprovalDialog';

interface OpenTasksTableProps {
  tasks: TaskListItem[];
  isLoading: boolean;
  /** Persists sorting and filters in the URL; needs a route whose search schema allows it. */
  enableUrlState?: boolean;
}

/**
 * The caller's open Aufgaben with an "Erledigt" action per row. Shared by the
 * customer "Meine Aufgaben" page and the partner "Aufgaben" page, which differ
 * only in the frame around it.
 */
export function OpenTasksTable({ tasks, isLoading, enableUrlState = false }: OpenTasksTableProps) {
  const { t } = useTranslation();
  const completeTask = useCompleteTask();
  const pendingTaskId = completeTask.isPending ? completeTask.variables.taskId : null;

  const handleComplete = useCallback(
    (task: TaskListItem) => {
      completeTask.mutate(
        { taskId: task._id },
        {
          onSuccess: () => toast.success(t('tasks.completed', { title: task.title })),
          onError: (error) => toastApiError(error, 'tasks.completeFailed')
        }
      );
    },
    [completeTask, t]
  );

  const [reviewing, setReviewing] = useState<string | null>(null);
  const columns = useOpenTasksColumns({
    onComplete: handleComplete,
    onReview: setReviewing,
    pendingTaskId
  });

  return (
    <>
      <DataTable
        columns={columns}
        data={tasks}
        isLoading={isLoading}
        emptyMessage={t('tasks.empty')}
        enableGlobalFilter
        globalSearchPlaceholder={t('tasks.search')}
        enableColumnVisibility
        enableColumnFilters
        enableUrlState={enableUrlState}
        tableId="openTasks"
        initialSorting={[{ id: 'dueAt', desc: false }]}
      />
      <ApprovalDialog approvalId={reviewing} onClose={() => setReviewing(null)} />
    </>
  );
}
